import { useCallback, useMemo } from "react";
import {
  AlertTriangle,
  BarChart3,
  ClipboardCheck,
  Info,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import MistralGuidanceChip from "@/components/MistralGuidanceChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import {
  buildApaarBlockInsight,
  buildApaarBlockPendingStack,
  buildApaarPrioritySchoolCards,
  buildApaarSchoolActionMarkdown,
  buildApaarSchoolActionQueue,
  buildApaarSchoolActionTable,
  buildApaarSchoolHeroStats,
  buildApaarSchoolPrimaryAction,
  buildApaarSchoolStatusChips,
  buildApaarSchoolWorkstreams,
} from "@/utils/apaarDerived";
import "./ApaarSchoolActionTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const WORKSTREAM_ICONS = {
  consent: UserPlus,
  verify: ClipboardCheck,
  support: BarChart3,
  closure: ShieldCheck,
};

const QUEUE_ICONS = {
  red: UserPlus,
  purple: BarChart3,
  orange: ClipboardCheck,
};

const ApaarSchoolActionTab = ({
  overview = {},
  topPending = [],
  lowPerforming = [],
  blockData = [],
}) => {
  const hasData = Boolean(overview?.total_students || topPending?.length);

  const buildLocalMarkdown = useCallback(
    () => buildApaarSchoolActionMarkdown(overview, topPending, lowPerforming, blockData),
    [overview, topPending, lowPerforming, blockData]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        p: topPending?.length,
        l: lowPerforming?.length,
        g: overview?.generation_rate,
      }),
    [topPending?.length, lowPerforming?.length, overview?.generation_rate]
  );

  const {
    loading: aiLoading,
    pickPrimaryAction,
    provider,
    isMistral,
    buckets,
  } = useMistralNarrativeOverlay({
    kind: "apaar-school-action",
    title: "APAAR School Action Control Center",
    buildLocalMarkdown,
    context: { overview, topPending, lowPerforming, blockData },
    enabled: hasData,
    scopeKey,
  });

  const heroStats = useMemo(
    () => buildApaarSchoolHeroStats(topPending, lowPerforming, overview),
    [topPending, lowPerforming, overview]
  );
  const statusChips = useMemo(() => {
    const chips = buildApaarSchoolStatusChips(topPending, lowPerforming, blockData);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [topPending, lowPerforming, blockData, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(() => buildApaarSchoolPrimaryAction(topPending), [topPending]);
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const workstreams = useMemo(
    () => buildApaarSchoolWorkstreams(topPending, lowPerforming, overview),
    [topPending, lowPerforming, overview]
  );
  const priorityCards = useMemo(() => buildApaarPrioritySchoolCards(topPending), [topPending]);
  const blockStack = useMemo(() => buildApaarBlockPendingStack(topPending), [topPending]);
  const blockInsight = useMemo(() => buildApaarBlockInsight(topPending), [topPending]);
  const actionTable = useMemo(
    () => buildApaarSchoolActionTable(topPending, lowPerforming),
    [topPending, lowPerforming]
  );
  const localActionQueue = useMemo(
    () => buildApaarSchoolActionQueue(topPending, lowPerforming),
    [topPending, lowPerforming]
  );
  const actionQueue = useMemo(() => {
    if (aiLoading) return localActionQueue;
    const aiActions = bulletsToActionQueue(parseInsightBullets(buckets.actions), localActionQueue);
    return aiActions?.length ? aiActions : localActionQueue;
  }, [aiLoading, buckets.actions, localActionQueue]);

  return (
    <div className="aps-school-tab" data-testid="apaar-school-action">
      <section className="aps-hero-card" id="aps-school-hero">
        <div className="aps-hero-layout">
          <div>
            <div className="aps-command-main">
              <div
                className="aps-gauge"
                style={heroStats.gaugeStyle}
                aria-label={`School action load index ${heroStats.actionLoadPct} percent`}
              >
                <div>
                  <strong>{heroStats.actionLoadPct}%</strong>
                  <span>Action Load</span>
                </div>
              </div>
              <div className="aps-hero-copy">
                <h2>School Action Control Center</h2>
                <p>
                  Current view separates urgent high-pending schools from low-generation schools so
                  officers can act by root cause instead of scrolling dense tables.
                </p>
                <div className="aps-status-row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                  <MistralGuidanceChip provider={provider} loading={aiLoading} />
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`aps-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="aps-summary-note">
              <div className="aps-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary district action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="aps-metric-grid" aria-label="School action KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className={`aps-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="aps-section-title" id="aps-workstreams">
        <div>
          <h2>Action Workstreams</h2>
          <p>
            Each workstream converts the raw school list into a clear operational lane for consent,
            verification, generation support and escalation.
          </p>
        </div>
        <button
          type="button"
          className="aps-section-action"
          onClick={() => scrollToId("aps-priority-schools")}
        >
          Open priority list →
        </button>
      </div>

      <section className="aps-workstream-grid" aria-label="Action workstreams">
        {workstreams.map((card) => {
          const Icon = WORKSTREAM_ICONS[card.id] ?? ClipboardCheck;
          return (
            <article key={card.id} className="aps-work-card">
              <div className="aps-work-head">
                <div className={`aps-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`aps-status-dot ${card.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className="aps-work-score">
                <strong>{card.score}</strong>
                <span>{card.scoreLabel}</span>
              </div>
              <div className="aps-progress-line">
                <i style={{ width: `${card.progress}%`, background: card.progressColor }} />
              </div>
            </article>
          );
        })}
      </section>

      <div className="aps-section-title" id="aps-priority-schools">
        <div>
          <h2>Priority School Queue</h2>
          <p>
            The top school list is redesigned as action cards first, highlighting pending load,
            generation rate and required follow-up instead of forcing users to scan every column.
          </p>
        </div>
        <button
          type="button"
          className="aps-section-action"
          onClick={() => scrollToId("aps-detailed-table")}
        >
          View detailed table →
        </button>
      </div>

      <section className="aps-analytics-grid">
        <article className="aps-panel">
          <div className="aps-panel-head">
            <div>
              <h3>Highest Pending Schools</h3>
              <p>Prioritized by pending count and current generation risk.</p>
            </div>
            <span className="aps-pill">Top 5</span>
          </div>
          <div className="aps-school-list">
            {priorityCards.map((school) => (
              <div key={school.udise_code} className="aps-school-card">
                <div className="aps-rank">{school.rank}</div>
                <div className="aps-school-name">
                  <b>
                    <SchoolLink udiseCode={school.udise_code}>{school.school_name}</SchoolLink>
                  </b>
                  <span>
                    {school.block_name} · {school.students} students
                  </span>
                </div>
                <div className="aps-mini-kpi">
                  <small>Pending</small>
                  <strong>{school.pending}</strong>
                </div>
                <div className="aps-small-track" title={`${school.genRate}% generation`}>
                  <i
                    style={{
                      width: `${school.trackWidth}%`,
                      background: school.trackColor,
                    }}
                  />
                </div>
                <span className={`aps-action-tag ${school.actionTagClass}`.trim()}>
                  {school.actionTag}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="aps-panel">
          <div className="aps-panel-head">
            <div>
              <h3>Pending Concentration by Block</h3>
              <p>Blocks where school-level action will clear the maximum backlog.</p>
            </div>
            <span className="aps-pill">Backlog</span>
          </div>
          <div className="aps-block-stack">
            {blockStack.map((row) => (
              <div key={row.name} className="aps-block-row">
                <b>{row.name}</b>
                <div className="aps-block-track">
                  <div
                    className={`aps-block-fill ${row.fillClass}`.trim()}
                    style={{ width: `${row.widthPct}%` }}
                  />
                </div>
                <span>{row.pending}</span>
              </div>
            ))}
          </div>
          <div className="aps-insight-box">
            <div className="aps-icon-box">
              <Info size={22} />
            </div>
            <div>
              <b>Operational insight</b>
              <span>{blockInsight}</span>
            </div>
          </div>
        </article>
      </section>

      <div className="aps-section-title" id="aps-detailed-table">
        <div>
          <h2>Detailed School Action Table</h2>
          <p>
            Table remains available for operational review, but with stronger hierarchy, compact
            status badges and next-action labels.
          </p>
        </div>
        <button
          type="button"
          className="aps-section-action"
          onClick={() => scrollToId("aps-school-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="aps-two-column">
        <article className="aps-panel aps-table-card">
          <div className="aps-table-head">
            <div>
              <h3>Schools Requiring Immediate Follow-Up</h3>
              <p>High pending or low generation schools requiring block officer intervention.</p>
            </div>
            <span className="aps-pill">Action list</span>
          </div>
          <div className="aps-table-wrap">
            <table aria-label="Schools requiring immediate follow-up">
              <thead>
                <tr>
                  <th>#</th>
                  <th>School</th>
                  <th>Block</th>
                  <th>Students</th>
                  <th>Generated</th>
                  <th>Pending</th>
                  <th>Gen %</th>
                  <th>Status</th>
                  <th>Next Action</th>
                </tr>
              </thead>
              <tbody>
                {actionTable.map((row) => (
                  <tr key={row.udise_code}>
                    <td>{row.rank}</td>
                    <td className="aps-school-link">
                      <SchoolLink udiseCode={row.udise_code}>{row.school_name}</SchoolLink>
                    </td>
                    <td>
                      <BlockLink blockCode={row.block_code} blockName={row.block_name}>
                        {row.block_name}
                      </BlockLink>
                    </td>
                    <td className="aps-num">{row.total_student}</td>
                    <td className="aps-num">{row.total_generated}</td>
                    <td className="aps-num">{row.pending}</td>
                    <td>{row.generation_rate}</td>
                    <td>
                      <span className={`aps-tag ${row.tagClass}`.trim()}>{row.status}</span>
                    </td>
                    <td>{row.nextAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="aps-panel">
          <div className="aps-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Focused actions for district and block teams.</p>
            </div>
            <span className="aps-pill">48-hour plan</span>
          </div>
          <div className="aps-action-card">
            {actionQueue.map((item) => {
              const Icon = QUEUE_ICONS[item.iconClass] ?? ClipboardCheck;
              return (
                <div key={item.title} className="aps-action-item">
                  <div className={`aps-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                    <div className="aps-action-meta">
                      {item.tags.map((tag) => (
                        <span key={tag.label} className={`aps-tag ${tag.className}`.trim()}>
                          {tag.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="aps-action-footer">
              <b>Next review recommendation</b>
              <span>
                Review the action queue every evening and publish block-wise closure progress for
                schools with more than 800 pending APAAR records.
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default ApaarSchoolActionTab;
